import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Info } from 'lucide-react';
import { Button } from '../ui/Button';

interface HardRefreshModalProps {
  isOpen: boolean;
  onRefresh?: () => void;
}

export function HardRefreshModal({
  isOpen,
  onRefresh
}: HardRefreshModalProps) {
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      window.location.reload();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - non-dismissible */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              scale: 0.95
            }}
            transition={{
              duration: 0.2
            }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-surface-900 border border-primary-500/30 rounded-xl shadow-2xl z-50"
          >
            {/* Header */}
            <div className="flex items-start gap-4 p-6 border-b border-surface-600">
              <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                <Info className="w-6 h-6 text-primary-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-text-primary mb-2">
                  Refresh Required
                </h2>
                <p className="text-sm text-text-secondary leading-relaxed">
                  The stream has been updated and your local view is out of sync.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-primary-200 leading-relaxed">
                  To ensure you're working with the latest data and avoid conflicts,
                  please refresh the page. Any unsaved changes may be lost.
                </p>
              </div>

              <Button
                onClick={handleRefresh}
                className="w-full"
                leftIcon={<RefreshCw className="w-4 h-4" />}
                size="lg"
              >
                Refresh Page
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
